import type { UserMap } from '../types/messages';
import type { Participant } from '../types/state';

/**
 * Transforms a UserMap from WebSocket messages into a Participant array for Redux Entity Adapter.
 *
 * The backend sends user data as an object keyed by userId (e.g., `{ "123": { name, avatarLink, role } }`).
 * This function converts that structure to an array of Participant objects with numeric IDs,
 * which is the format expected by our Entity Adapter for normalized state management.
 *
 * @param userMap - Object mapping userId strings to user info from WebSocket message
 * @returns Array of Participant objects suitable for Entity Adapter operations (upsertMany, setAll, etc.)
 *
 * @example
 * // Input from WebSocket
 * const userMap = {
 *   "123": { name: "Alice", avatarLink: "...", role: "participant" },
 *   "456": { name: "Bob", avatarLink: "...", role: "host" }
 * };
 *
 * // Output for Entity Adapter
 * const participants = transformUserMapToParticipants(userMap);
 * // [{ id: 123, name: "Alice", ... }, { id: 456, name: "Bob", ... }]
 */
export const transformUserMapToParticipants = (userMap: UserMap): Participant[] =>
  Object.entries(userMap).map(([userId, userInfo]) => ({
    id: Number(userId),
    name: userInfo.name,
    avatarLink: userInfo.avatarLink,
    role: userInfo.role,
  }));

/**
 * Extracts a single user from a UserMap containing exactly one entry.
 *
 * Used for broadcast messages that notify about a single user action
 * (e.g., OtherUserJoinedWaitingRoom, OtherUserJoinedRoom) where the backend
 * sends the user data wrapped in a single-entry object.
 *
 * @param userMap - Object containing exactly one userId -> user info mapping
 * @returns Single Participant object
 * @throws Error if userMap is empty
 *
 * @example
 * // Input from OtherUserJoinedWaitingRoom message
 * const userMap = { "789": { name: "Charlie", avatarLink: "...", role: "participant" } };
 *
 * // Output
 * const participant = extractSingleUserFromMap(userMap);
 * // { id: 789, name: "Charlie", avatarLink: "...", role: "participant" }
 */
export const extractSingleUserFromMap = (userMap: UserMap): Participant => {
  const entries = Object.entries(userMap);
  if (entries.length === 0) {
    throw new Error('extractSingleUserFromMap: received empty userMap');
  }
  const [userId, userInfo] = entries[0];
  return {
    id: Number(userId),
    name: userInfo.name,
    avatarLink: userInfo.avatarLink,
    role: userInfo.role,
  };
};
